import { Root } from './rootModel'
import { Address } from './addressModels'
import { MemberPrivileges } from './memberModel'
import { Identifier, IdentifierType } from './debtorModels'
import { Currency } from './moneyModel'
import { Country } from './countryModels'
import { InvoiceOwner } from './invoiceModels'

export const paymentRails = ['SCT', 'SCTINST', 'FASTER_PAYMENTS', 'SWIFT'] as const
export type PaymentRail = (typeof paymentRails)[number]

export const guarantorOptions = ['aria', 'company'] as const
export type Guarantor = (typeof guarantorOptions)[number]

export type RegistrationNumber = {
    id: string
    type: IdentifierType
    value: string
    ownerId: string
    country?: Country['code']
    companyId?: string
}

export interface ContractInfos {
    templateId: string
    textTabsLabels: string[]
    roleName: string
}

export type PaymentDestination = {
    type: 'BANK_ACCOUNT' | 'BANK_ACCOUNT_SCAN'
    accountNumberType: 'IBAN' | 'ACCOUNT_NUMBER'
    accountNumber: string
    routingNumberType?: 'BIC' | 'SORT_CODE'
    routingNumber?: string
}

export type PaymentDestinationForm = {
    type: PaymentDestination['type'] | null
    accountNumberType: PaymentDestination['accountNumberType'] | null
    accountNumber: string
    routingNumberType: PaymentDestination['routingNumberType'] | null
    routingNumber: string
}

export const paymentTermOptionsList = ['net', 'eom', 'eomf'] as const
export type PaymentTermOption = (typeof paymentTermOptionsList)[number]

export function isPaymentTermOption(key: string): key is PaymentTermOption {
    return key === 'net' || key === 'eom' || key === 'eomf'
}

export type PaymentTermOptionType = {
    enabled: boolean
    min?: number
    max?: number
    enum?: number[]
}

export interface CompanyPerCurrencyConfig {
    currency: Currency
    repaymentDestinations: PaymentDestination[]
    overrideLoanPaymentDestination?: PaymentDestination
    overrideLoanPayoutLabel?: string
    availablePaymentTermOptions?: PaymentTermOption[]
    paymentTermOptions?: {
        net: PaymentTermOptionType | null
        eom: PaymentTermOptionType | null
        eomf: PaymentTermOptionType | null
    }
    defaultGuarantor?: Guarantor
    defaultInvoiceOwner?: InvoiceOwner
    defaultInvoiceIssuer?: 'beneficiary' | 'company'
    availablePaymentRails?: PaymentRail[]
    defaultPaymentRail?: PaymentRail
}

export interface TimeInterval {
    value: number
    label: string
}

export interface CompanyConfigAttributes {
    advanceDurations?: number[]
    isCompanyClient: boolean
    hasContractInfos: boolean
    contractInfos?: ContractInfos
    overrideIBAN?: string
    overrideLabel?: string
    currencyConfigs: CompanyPerCurrencyConfig[]
    signerMemberIds?: string[]
    isGuarantorEnabled?: boolean
    needsKyc?: boolean
    areQuotesAvailable: boolean
    creditMemo: {
        enabled: boolean
    }
    receivable: {
        enabled: boolean
        maxInvoiceDelayInDays: number
    }
    repayments: {
        enabled: boolean
    }
}

export type Company = Root & {
    name: string
    config: CompanyConfigAttributes
    refundIBAN?: string
    refundBIC?: string
    ibanSwan?: string
    logo?: string
    address: Address
    identifier: Identifier
}

export interface UpdateCompany {
    privileges?: MemberPrivileges
    name: Company['name']
    identifier: Identifier
    address: Pick<Company['address'], 'first' | 'postal' | 'city' | 'country'>
}

export interface UpdateCompanyGateway {
    privileges?: MemberPrivileges
    name: Company['name']
    identifier: Identifier
    address: Pick<Company['address'], 'first' | 'postal' | 'city' | 'country'>
}
